/* Sticker Select — multiple designs on sticker sheets.
 * On the Sticker Sheets product the customer can add up to 8 designs. Each extra design uses the same
 * private uploader as the first one, and is saved on the order as "Artwork 2", "Artwork 2 ID", etc.
 * Display + order info only — never affects price.
 */
(() => {
  'use strict';
  const MAX = 8;
  const isSheet = (el) => {
    const cut = String(el.dataset.choiceCutType || '');
    const handle = String((el.product && el.product.handle) || '');
    return cut === 'sticker sheet' || /sheet/.test(handle);
  };

  const makeSlot = (tpl, n) => {
    const u = tpl.cloneNode(true);
    u.removeAttribute('data-required');
    u.classList.remove('is-ready', 'is-error');
    u.querySelectorAll('[id]').forEach((x) => { x.id = `${x.id}-d${n}`; });
    u.querySelectorAll('label[for]').forEach((l) => { l.htmlFor = `${l.htmlFor}-d${n}`; });
    u.querySelectorAll('[aria-describedby]').forEach((x) => {
      x.setAttribute('aria-describedby', x.getAttribute('aria-describedby').split(/\s+/).filter(Boolean).map((s) => `${s}-d${n}`).join(' '));
    });
    u.querySelectorAll('input[type="hidden"]').forEach((x) => x.remove());
    const drop = u.querySelector('[data-drop]'); if (drop) { drop.hidden = false; drop.classList.remove('is-drag'); }
    const card = u.querySelector('[data-file-card]'); if (card) card.hidden = true;
    ['[data-upload-error]', '[data-quality-warning]', '[data-progress]'].forEach((s) => { const x = u.querySelector(s); if (x) { x.hidden = true; x.textContent = ''; } });
    const thumb = u.querySelector('[data-thumb]'); if (thumb) { thumb.removeAttribute('src'); thumb.hidden = true; }
    const ti = u.querySelector('[data-thumb-icon]'); if (ti) ti.hidden = false;
    const fn = u.querySelector('[data-file-name]'); if (fn) fn.textContent = '';
    const fs = u.querySelector('[data-file-status]'); if (fs) fs.textContent = '';
    const guide = u.querySelector('.upload__guide'); if (guide) guide.remove();
    const input = u.querySelector('[data-file-input]'); if (input) { input.value = ''; input.removeAttribute('aria-invalid'); }
    u.hidden = true;
    return u;
  };

  customElements.whenDefined('product-configurator').then(() => {
    const proto = customElements.get('product-configurator').prototype;
    if (proto._ssSheets) return;
    proto._ssSheets = true;

    const filledExtras = (el) => (el._sheetSlots || []).filter((u) => !u.hidden && u.state && u.state.status !== 'empty');

    // Validation: extra designs are optional, but must not be mid-upload or failed.
    const origErrors = proto.collectErrors;
    proto.collectErrors = function (...args) {
      const errs = origErrors.apply(this, args);
      (this._sheetSlots || []).filter((u) => !u.hidden).forEach((u, i) => {
        const s = u.state || {};
        const input = u.querySelector('[data-file-input]');
        if (s.status === 'uploading') errs.push({ msg: `Please wait for design ${i + 2} to finish uploading.`, el: input });
        else if (s.status === 'error') errs.push({ msg: `Design ${i + 2} didn't upload. Remove it or try again.`, el: input });
      });
      return errs;
    };

    // Order details: Artwork 2 / Artwork 2 ID … Artwork 8 / Artwork 8 ID.
    const origProps = proto.buildProperties;
    proto.buildProperties = function (...args) {
      const p = origProps.apply(this, args);
      if (this._sheetSlots) {
        let n = p['Artwork ID'] ? 1 : 0;
        this._sheetSlots.forEach((u) => {
          const v = !u.hidden && u.value;
          if (v) { n += 1; p[`Artwork ${n}`] = v.filename; p[`Artwork ${n} ID`] = v.ref; }
        });
        if (n > 1) p['Designs on sheet'] = String(n);
      }
      return p;
    };

    // The size estimate describes a single design, so hide it once a sheet has more than one.
    if (proto.sizeEstimate) {
      const origEst = proto.sizeEstimate;
      proto.sizeEstimate = function (...args) {
        if (this._sheetSlots && filledExtras(this).length) return null;
        return origEst.apply(this, args);
      };
    }

    const setup = (el) => {
      if (el._sheetInit || !el.upload || !isSheet(el)) return;
      el._sheetInit = true;
      const first = el.upload;
      const tpl = first.cloneNode(true);

      const wrap = document.createElement('div');
      wrap.className = 'sheet-uploads';
      wrap.style.cssText = 'display:grid;gap:.6rem;margin-top:.6rem';
      const addBtn = document.createElement('button');
      addBtn.type = 'button';
      addBtn.className = 'btn btn--secondary btn--sm';
      addBtn.style.justifySelf = 'start';
      const hint = document.createElement('p');
      hint.className = 'hint';
      hint.style.margin = '0';
      hint.setAttribute('aria-live', 'polite');

      el._sheetSlots = [];
      for (let n = 2; n <= MAX; n++) {
        const u = makeSlot(tpl, n);
        wrap.appendChild(u);
        el._sheetSlots.push(u);
      }
      wrap.appendChild(addBtn);
      wrap.appendChild(hint);
      first.insertAdjacentElement('afterend', wrap);

      const cta = first.querySelector('.upload__cta strong');
      if (cta) cta.textContent = 'Upload your designs';

      let skipped = 0;
      const refresh = () => {
        const visible = el._sheetSlots.filter((u) => !u.hidden);
        visible.forEach((u, i) => {
          const c = u.querySelector('.upload__cta strong'); if (c) c.textContent = `Upload design ${i + 2}`;
          const rm = u.querySelector('[data-remove]'); if (rm) rm.setAttribute('aria-label', `Remove design ${i + 2}`);
        });
        const total = 1 + visible.length;
        const hasEmpty = visible.some((u) => !u.state || u.state.status === 'empty');
        const firstFilled = first.state && first.state.status !== 'empty';
        addBtn.hidden = total >= MAX || hasEmpty || !firstFilled;
        addBtn.textContent = `+ Add another design (${total} of ${MAX})`;
        hint.textContent = skipped
          ? `Sticker sheets fit up to ${MAX} designs, so ${skipped} extra file${skipped > 1 ? 's were' : ' was'} skipped.`
          : `Sticker sheets can include up to ${MAX} different designs. You can select several files at once.`;
      };

      const nextSlot = () => {
        const empty = el._sheetSlots.find((u) => !u.hidden && (!u.state || u.state.status === 'empty'));
        if (empty) return empty;
        const hidden = el._sheetSlots.find((u) => u.hidden);
        if (hidden) { hidden.hidden = false; return hidden; }
        return null;
      };

      // Several files picked or dropped at once: file 1 goes where it was dropped, the rest fill the next slots.
      const spread = (files) => {
        skipped = 0;
        files.forEach((f) => {
          const slot = nextSlot();
          if (slot) slot.handleFile(f); else skipped += 1;
        });
        refresh();
      };
      [first, ...el._sheetSlots].forEach((u) => {
        const input = u.querySelector('[data-file-input]');
        if (input) {
          input.multiple = true;
          input.addEventListener('change', () => { const fl = input.files ? [...input.files] : []; if (fl.length > 1) spread(fl.slice(1)); });
        }
        const drop = u.querySelector('[data-drop]');
        if (drop) drop.addEventListener('drop', (ev) => { const fl = ev.dataTransfer && ev.dataTransfer.files ? [...ev.dataTransfer.files] : []; if (fl.length > 1) spread(fl.slice(1)); });
      });

      // A removed extra design closes its slot and moves to the end, so the list stays tidy.
      el._sheetSlots.forEach((u) => {
        u.addEventListener('artwork:change', () => {
          if (u.state && u.state.status === 'empty' && !u.hidden && u.querySelector('[data-upload-error]').hidden) {
            u.hidden = true;
            wrap.insertBefore(u, addBtn);
            el._sheetSlots.splice(el._sheetSlots.indexOf(u), 1);
            el._sheetSlots.push(u);
          }
          refresh();
        });
      });
      first.addEventListener('artwork:change', refresh);

      addBtn.addEventListener('click', () => {
        skipped = 0;
        const slot = nextSlot();
        refresh();
        if (slot) { const input = slot.querySelector('[data-file-input]'); if (input) input.click(); }
      });

      // Editing a cart line: bring back the extra designs.
      const editKey = new URLSearchParams(window.location.search).get('edit');
      if (editKey && window.SS && SS.routes) {
        fetch(`${SS.routes.cart}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((cart) => {
            const item = (cart.items || []).find((i) => i.key === editKey);
            const props = (item && item.properties) || {};
            for (let n = 2; n <= MAX; n++) {
              if (!props[`Artwork ${n} ID`]) continue;
              const slot = nextSlot();
              if (slot) slot.setPreset({ ref: props[`Artwork ${n} ID`], filename: props[`Artwork ${n}`] });
            }
            refresh();
          })
          .catch(() => { /* ignore */ });
      }

      refresh();
    };

    document.querySelectorAll('product-configurator').forEach(setup);
  });
})();
